import type { FigureTheme } from "~/pages/manifold-nextgen/hooks/useFigureTheme";

// Material Symbols `light_mode` / `dark_mode` (Apache-2.0), 24x24 filled paths.
// Plotly themes a glyph by setting `fill` on `.icon path`, and only the
// {width, height, path} icon form gets that class — an `icon.svg` string is
// inserted verbatim and stays unthemed, as would a stroked lucide glyph.
const CHART_THEME_ICONS: Record<FigureTheme, { width: number; height: number; path: string }> = {
  light: {
    width: 24,
    height: 24,
    path: "M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5M2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1m18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1M11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1m0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1M5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0z",
  },
  dark: {
    width: 24,
    height: 24,
    path: "M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1",
  },
};

export const CHART_THEME_BUTTON_NAME = "toggleChartTheme";

/**
 * The modebar button that flips the plot theme, labelled with where the click
 * leads rather than where you are.
 *
 * Goes into `config.modeBarButtons` directly — plotly ignores
 * `modeBarButtonsToAdd` whenever an explicit list is given, and that list takes
 * objects as well as names. `toggle: true` is deliberately unused: plotly would
 * flip `.active` itself while React rebuilds the modebar from the new
 * preference, leaving two owners of one bit.
 */
export const chartThemeButton = (figureTheme: FigureTheme, onClick: () => void) => {
  const next: FigureTheme = figureTheme === "dark" ? "light" : "dark";
  return {
    name: CHART_THEME_BUTTON_NAME,
    // "Plot" matches the Appearance tab's wording for the same setting.
    title: next === "dark" ? "Dark plot theme" : "Light plot theme",
    icon: CHART_THEME_ICONS[next],
    click: onClick,
  };
};
